import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallbackContext?: string;
}

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Fängt unhandled React-Errors ab, damit ein Crash in einer Komponente
 * nicht zu einer komplett weißen Seite fuehrt. Zeigt stattdessen eine
 * sichtbare Error-Box mit Details — fuer User-Reports und Selbst-Debugging.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.error) {
      const ctx = this.props.fallbackContext || 'Seite';
      return (
        <section className="py-8 md:py-16 bg-red-50 min-h-screen">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg border-2 border-red-200 p-6 md:p-8 shadow-sm">
              <h1 className="text-2xl font-bold text-red-900 mb-4">
                Fehler beim Anzeigen ({ctx})
              </h1>
              <p className="text-gray-700 mb-4">
                Ein technischer Fehler ist aufgetreten. Bitte laden Sie die Seite neu (
                <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 border border-gray-300 rounded">Cmd+Shift+R</kbd>{' '}
                /{' '}
                <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 border border-gray-300 rounded">Ctrl+Shift+R</kbd>
                ) oder kehren Sie zur Startseite zurück.
              </p>
              <details className="mt-4 text-sm text-gray-600 bg-gray-50 rounded p-3">
                <summary className="cursor-pointer font-medium">Technische Details (für Bug-Report)</summary>
                <p className="mt-3 font-mono text-xs whitespace-pre-wrap break-all">
                  <strong>Fehler:</strong> {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <p className="mt-2 font-mono text-xs whitespace-pre-wrap break-all">
                    <strong>Stack:</strong>{'\n'}{this.state.error.stack.split('\n').slice(0, 8).join('\n')}
                  </p>
                )}
                {this.state.errorInfo?.componentStack && (
                  <p className="mt-2 font-mono text-xs whitespace-pre-wrap break-all">
                    <strong>Komponenten-Pfad:</strong>{'\n'}{this.state.errorInfo.componentStack.split('\n').slice(0, 8).join('\n')}
                  </p>
                )}
              </details>
              <div className="mt-6">
                <a href="/" className="text-blue-600 hover:text-blue-800 font-medium">← Zur Startseite</a>
              </div>
            </div>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}
